/* eslint-disable max-classes-per-file */
/* eslint-disable no-underscore-dangle */
import request from "src/utils/request";
import JWT from "src/components/providers/jwt";
import type { JWTInterface, Token, User } from "src/services/types";
import { identify as identifyFS } from "../utils/tracking/fullstory";
import { identify as identifyH } from "../utils/tracking/highlight";
import identifyGtag from "../utils/tracking/gtag";
import { getBaseUrl } from "../utils/url";
import admins from "./admins";

const SYSTEM_ORIGIN = "plg";

class Authentication {
    isAuthenticated: boolean;
    user?: User;
    tokenRefreshPromise: Promise<Token> | null;
    _token?: Token;
    jwt?: JWTInterface;
    idToken?: {
        mergedIdentity: string;
        isDemo: boolean;
        plans: string[];
        sub: string;
    };

    throttledLogout: Promise<unknown> | undefined;

    constructor() {
        this.isAuthenticated = false;
        this.token = Authentication.getTokenFromLocalStorage();
        this.user = undefined;
        this.tokenRefreshPromise = null;
    }

    set token(value) {
        this._token = value;
        if (value && value.access_token) {
            try {
                this.jwt = new JWT(value.access_token);
                this.idToken = new JWT(value.id_token);
            } catch (_e) {
                this.logout();
            }
        }
    }

    get token() {
        this.token = Authentication.getTokenFromLocalStorage();
        return this._token;
    }

    // eslint-disable-next-line consistent-return
    async authenticate(code) {
        const alreadyAuthenticated = await this.checkAuthenticated();
        if (alreadyAuthenticated) return Promise.resolve();
        await this.getToken(code);
        await this.getUser();
        identifyFS(this.jwt, this.user);
        identifyH(this.jwt, this.user);
        identifyGtag(this.jwt);
        this.isAuthenticated = true;
    }

    // eslint-disable-next-line consistent-return
    async checkAuthenticated(logoutWithoutRedirectOnUnauthorized = false) {
        if (this.isAuthenticated) return Promise.resolve(true);
        if (this.token) {
            await this.getUser(logoutWithoutRedirectOnUnauthorized);
            this.isAuthenticated = true;
            if (this.idToken && !this.idToken.isDemo) {
                identifyFS(this.jwt, this.user);
                identifyH(this.jwt, this.user);
                identifyGtag(this.jwt);
            }
        }
    }

    // eslint-disable-next-line class-methods-use-this
    getRedirectUrl(route?: string): string {
        const url = new URL(window.location.href);
        return `${url.origin}${route || url.pathname}${url.search}`;
    }

    getLoginUrl(route?: string, email?: string, connection?: string): string {
        return `${getBaseUrl()}/auth/login?redirect_uri=${this.getRedirectUrl(
            route
        )}&system_origin=${SYSTEM_ORIGIN}${email ? `&email=${email}` : ""}${
            connection ? `&connection=${connection}` : ""
        }`;
    }

    async logout(logoutWithoutRedirectOnUnauthorized = false, redirect = true) {
        this.isAuthenticated = false;
        localStorage.removeItem("token");
        sessionStorage.removeItem("highlightIdentifier");
        sessionStorage.removeItem("highlightUserObject");
        this.token = undefined;
        this.user = undefined;
        if (!logoutWithoutRedirectOnUnauthorized) {
            if (!this.throttledLogout) {
                this.throttledLogout = request<string>(
                    `/auth/logout${
                        redirect ? `?redirect_uri=${this.getRedirectUrl()}&` : "?"
                    }system_origin=${SYSTEM_ORIGIN}`,
                    { logoutWithoutRedirectOnUnauthorized }
                );
                const logoutUrl = await this.throttledLogout;
                window.location.replace(logoutUrl as string);
                this.throttledLogout = undefined;
                await new Promise((r) => {
                    setTimeout(r, 5000);
                });
            }
        }
    }

    // eslint-disable-next-line class-methods-use-this
    relog() {
        window.location.replace(`${getBaseUrl()}/login-username`);
    }

    async redirectToLogin() {
        await this.logout(true);
        window.location.replace(`${getBaseUrl()}/login-username`);
    }

    isTokenExpired(): boolean {
        return !!(this.jwt && this.jwt.exp <= Math.floor(new Date().valueOf() / 1000));
    }

    async requestRefreshToken() {
        try {
            const tokenRefreshPromise = request<Token>(
                `/auth/refresh?refresh_token=${this.token!.refresh_token}&redirect_uri=${
                    window.location.origin
                }${window.location.pathname}&system_origin=${SYSTEM_ORIGIN}`,
                // If token refresh fails, it's not going to pass next time. Use the
                // logoutWithoutRedirectOnUnauthorized option to bail out of the potential infinite
                // loop that can arise here.
                { logoutWithoutRedirectOnUnauthorized: true }
            );
            this.tokenRefreshPromise = tokenRefreshPromise;
            const token = await tokenRefreshPromise;
            localStorage.setItem("token", JSON.stringify(token));
            this.token = token;
            this.tokenRefreshPromise = null;
        } catch (_e) {
            await this.logout();
        }
    }

    // eslint-disable-next-line consistent-return
    async refreshToken() {
        if (this.tokenRefreshPromise !== null) {
            await this.tokenRefreshPromise;
            return true;
        }

        if (this.token && this.token.refresh_token) {
            await this.requestRefreshToken();
            await this.getUser();
            return true;
        }
        this.redirectToLogin();
    }

    relogOnPlanChange() {
        if (this.user?.plans || this.idToken?.plans) {
            if (this.user?.plans?.length !== this.idToken?.plans.length) {
                if (
                    this.user?.id !== this.idToken?.sub &&
                    admins.find((admin) => admin.userId === this.idToken?.sub)
                ) {
                    return undefined;
                }
                return this.relog();
            }
        }
        return undefined;
    }

    async getUser(logoutWithoutRedirectOnUnauthorized?: boolean) {
        if (this.idToken?.isDemo) {
            this.user = {
                user_id: "demo-user",
                orgId: "demo-org",
                emails: ["demo@adadot.com"],
                displayName: "Demo User",
                name: { givenName: "Demo", familyName: "User" },
                email: "demo@adadot.com",
                id: "demo-user",
                picture: "https://upload.wikimedia.org/wikipedia/commons/7/77/Avatar_cat.png",
                nickname: "demo",
                rank: 9,
                profileVisibility: "public",
                rankVisibility: "public",
                email_verified: false,
                onboarding_complete: false,
                location: { region: "", country: "" },
            };
            return this.user;
        }
        this.user = await request(`/api/users/me`, { logoutWithoutRedirectOnUnauthorized });
        this.relogOnPlanChange();
        return this.user;
    }

    async updateUser() {
        this.user = await request(`/api/users/me`);
        this.relogOnPlanChange();
        return this.user;
    }

    // eslint-disable-next-line consistent-return
    static getTokenFromLocalStorage() {
        const localStorageItem = window.localStorage.getItem("token");
        if (localStorageItem) {
            try {
                const localToken = JSON.parse(localStorageItem);
                if (localToken && localToken.access_token) {
                    return localToken;
                }
            } catch (_e) {
                localStorage.removeItem("token");
            }
        }
    }

    async getToken(code: string) {
        if (!code) throw new Error(`Can't request token without a code`);
        const token = await request<Token>(
            `/auth/token?code=${code}&redirect_uri=${window.location.origin}${window.location.pathname}&system_origin=${SYSTEM_ORIGIN}`
        );
        localStorage.setItem("token", JSON.stringify(token));
        this.token = token;
        return token;
    }
}

class AuthTest extends Authentication {
    reset() {
        this.user = undefined;
        this.token = undefined;
        this.jwt = undefined;
        this.idToken = undefined;
        this.isAuthenticated = false;
    }
}

const Auth = process.env.NODE_ENV === "test" ? new AuthTest() : new Authentication();

export default Auth;
